// Innings in the field and on the bench, per player, for one game: either a
// plan (innings as an array, inning 1 at index 0) or a live game (logged
// innings plus the inning being played). Pure and display-only.
//
// Same conventions as the fairness check and the field time report: BENCH and
// EH are not in the field; P, C and the four infield spots are infield.
//
// Deliberately NOT shared with lineup-engine.ts, which keeps its own counters
// inside the solver; its fill order is load-bearing and must not be touched
// to suit a display.

export interface FieldTimeCount {
  field: number;
  /** BENCH only. */
  bench: number;
  /** Extra hitter: bats, doesn't field. Not field time, but not sitting either. */
  eh: number;
  infield: number;
  outfield: number;
  pitched: number;
}

type Entry = { id: string; position: string };

const INFIELD = new Set(['P', 'C', '1B', '2B', 'SS', '3B']);
export const isBenched = (pos: string | undefined) => !pos || pos === 'BENCH' || pos === 'EH';

/**
 * Count each player's innings across the given innings. A player missing from
 * an inning's list counts as bench for it (the planner's convention: unlisted
 * means not placed).
 */
export function countFieldTime(innings: (Entry[] | undefined)[], playerIds: string[]): Map<string, FieldTimeCount> {
  const counts = new Map<string, FieldTimeCount>(
    playerIds.map(id => [id, { field: 0, bench: 0, eh: 0, infield: 0, outfield: 0, pitched: 0 }]),
  );
  innings.forEach(inning => {
    playerIds.forEach(id => {
      const c = counts.get(id)!;
      const pos = inning?.find(e => e.id === id)?.position;
      if (pos === 'EH') { c.eh++; return; }
      if (isBenched(pos)) { c.bench++; return; }
      c.field++;
      if (INFIELD.has(pos!)) c.infield++; else c.outfield++;
      if (pos === 'P') c.pitched++;
    });
  });
  return counts;
}

/** The cells that differ between two versions of the same innings, as "inningIdx:playerId". */
export function changedCells(before: (Entry[] | undefined)[], after: (Entry[] | undefined)[]): Set<string> {
  const out = new Set<string>();
  const n = Math.max(before.length, after.length);
  for (let i = 0; i < n; i++) {
    const a = before[i] || [];
    const b = after[i] || [];
    const ids = new Set([...a.map(e => e.id), ...b.map(e => e.id)]);
    ids.forEach(id => {
      const pa = a.find(e => e.id === id)?.position ?? 'BENCH';
      const pb = b.find(e => e.id === id)?.position ?? 'BENCH';
      if (pa !== pb) out.add(`${i}:${id}`);
    });
  }
  return out;
}
